import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Search, Plus, Mail, Users, FileSpreadsheet, KeyRound, Heart, RefreshCw, Briefcase, ChevronsUpDown } from 'lucide-react';
import { OrgImportWizard } from '@/components/client/OrgImportWizard';
import { useApp } from '@/context/AppContext';
import { ListGridToggle } from '@/components/shared/ListGridToggle';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { useSort } from '@/lib/useSort';
import { useDataLabel } from '@/lib/dataI18n';
import { api, formatApiError } from '@/lib/api';
import { fmtDateTimePA } from '@/lib/panamaTime';
import { toast } from 'sonner';

/**
 * Issue 4 — wired to real backend.
 * GET /api/client/users · POST /api/client/users
 * GET /api/client/departments
 */
const initials = (name) => (name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

/** B1.3 — searchable formal-position picker (persists users.position_id). */
export const FormalPositionPicker = ({ positions, value, onChange, es, testid }) => {
  const [open, setOpen] = useState(false);
  const sel = positions.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={testid}>
          <span className="truncate flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {sel ? sel.title : (es ? '— Sin puesto formal vinculado' : '— No formal position linked')}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 z-[80]" align="start">
        <Command>
          <CommandInput placeholder={es ? 'Buscar puesto formal…' : 'Search formal position…'} data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{es ? 'Sin resultados. Crea puestos en Departamentos → Gestionar puestos.' : 'No results. Create positions in Departments → Manage positions.'}</CommandEmpty>
            <CommandGroup>
              <CommandItem value="sin puesto formal none" onSelect={() => { onChange(''); setOpen(false); }}>
                {es ? '— Sin puesto formal' : '— No formal position'}
              </CommandItem>
              {positions.map((p) => (
                <CommandItem key={p.id} value={`${p.title} ${p.department_name || ''}`}
                             onSelect={() => { onChange(p.id); setOpen(false); }}
                             data-testid={testid ? `${testid}-opt-${p.id}` : undefined}>
                  <span className="flex flex-col min-w-0">
                    <span className="truncate">{p.title}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {p.department_name || ''}{p.role_profile_id ? (es ? ' · perfil de puesto vinculado' : ' · job profile linked') : ''}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const SORT_KEYS = {
  collaborator: (u) => (u.name || '').toLowerCase(),
  position:     (u) => (u.position || '').toLowerCase(),
  department:   (u) => (u.department_name || '').toLowerCase(),
  contact:      (u) => (u.email || '').toLowerCase(),
};

// TalentHeart 2026-06 — per-collaborator profile request states.
const TH_STATE_META = {
  pending:            { key: 'pending',           cls: 'bg-warning/15 text-warning border-warning/30' },
  completed:          { key: 'completed',         cls: 'bg-success/15 text-success border-success/30' },
  update_requested:   { key: 'updateRequested',   cls: 'bg-info/15 text-info border-info/30' },
  invitation_invalid: { key: 'invitationInvalid', cls: 'bg-destructive/10 text-destructive border-destructive/30' },
  // CORE-UAT-02 — an empty profile doc means the employee skipped/declined.
  skipped:            { key: 'skipped',           cls: 'bg-muted text-muted-foreground border-border' },
};

function ThProfileAction({ u, state, busy, canRequest, onRequest, t }) {
  const eligible = (!u.role || u.role === 'evaluated') && u.status !== 'inactive' && !u.external_candidate;
  if (!eligible) return null;
  const meta = TH_STATE_META[state];
  const isOpen = state === 'pending' || state === 'update_requested';
  return (
    <>
      {meta && (
        <Badge variant="outline" className={`text-xs ${meta.cls}`} data-testid={`collab-th-state-${u.id}`}>
          <Heart className="h-2.5 w-2.5 mr-1" />
          {t(`pages.collaborators.thProfile.state.${meta.key}`)}
        </Badge>
      )}
      {canRequest && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          className="text-pink-600 border-pink-200 hover:bg-pink-50"
          title={isOpen
            ? t('pages.collaborators.thProfile.remind')
            : state === 'completed'
              ? t('pages.collaborators.thProfile.requestUpdate')
              : t('pages.collaborators.thProfile.request')}
          data-testid={`collab-th-request-${u.id}`}
          onClick={() => onRequest(u, state)}
        >
          {isOpen ? <Mail className="h-3.5 w-3.5" />
            : state === 'completed' ? <RefreshCw className="h-3.5 w-3.5" />
              : <Heart className="h-3.5 w-3.5" />}
        </Button>
      )}
    </>
  );
}

export default function ClientEmployees() {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const tData = useDataLabel();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [tab, setTab] = useState('active');
  const [view, setView] = useState('grid');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [knowPrefs, setKnowPrefs] = useState(null);
  useEffect(() => {
    setKnowPrefs(null);
    if (viewing?.id && (!viewing.role || viewing.role === 'evaluated') && !viewing.external_candidate) {
      api.get(`/client/theart/employee/${viewing.id}/preferences`)
        .then((r) => setKnowPrefs(r.data)).catch(() => setKnowPrefs(null));
    }
  }, [viewing]);
  // Founder P1 2026-09 — Editar colaborador (same identity, no duplicates).
  const [editDraft, setEditDraft] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const saveEdit = async () => {
    if (!editDraft) return;
    setEditSaving(true);
    try {
      const { data } = await api.put(`/client/users/${editDraft.id}`, {
        name: editDraft.name,
        email: editDraft.email,
        department_id: editDraft.department_id || '',
        position: editDraft.position,
        position_id: editDraft.position_id || '',
      });
      toast.success(t('pages.collaborators.edit.saved', { defaultValue: 'Colaborador actualizado correctamente.' }));
      setEditDraft(null);
      setViewing(data);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setEditSaving(false); }
  };
  const [draft, setDraft] = useState({ name: '', email: '', position: '', department_id: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [createdCreds, setCreatedCreds] = useState(null);
  const [issuingCreds, setIssuingCreds] = useState(null);
  const issuingRef = React.useRef(false);

  // UAT — Portal Access lifecycle: Client Admin issues/resets temp credentials
  // for evaluated collaborators inside their own tenant.
  const doIssueCreds = async (u) => {
    if (issuingRef.current) return;  // guard: double-click would rotate the password twice
    issuingRef.current = true;
    setIssuingCreds(u.id);
    try {
      const { data } = await api.post(`/client/users/${u.id}/reset-password`);
      setCreatedCreds({ email: data.email, password: data.temp_password, name: data.name });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { issuingRef.current = false; setIssuingCreds(null); }
  };
  const [people, setPeople] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const sort = useSort();

  // TalentHeart 2026-06 — profile request states + heart action.
  const canRequestTh = ['client_admin', 'client_hr'].includes(user?.role);
  const [thStates, setThStates] = useState({});
  const [thQuestionnaire, setThQuestionnaire] = useState(null);
  const [thBusy, setThBusy] = useState(null);
  const [confirmUpdateFor, setConfirmUpdateFor] = useState(null);
  const loadThStates = async () => {
    try {
      const { data } = await api.get('/client/theart/profile-request-status');
      setThStates(data?.states || {});
      setThQuestionnaire(data?.questionnaire || null);
    } catch { /* non-blocking */ }
  };
  const doRequestThProfile = async (u) => {
    setThBusy(u.id);
    try {
      const { data } = await api.post('/client/theart/profile-requests', { user_id: u.id });
      setThStates((prev) => ({
        ...prev,
        [u.id]: {
          ...(prev[u.id] || {}),
          state: data.state,
          requested_at: data.sent_at || prev[u.id]?.requested_at,
          reminded_at: data.reminded_at || prev[u.id]?.reminded_at,
        },
      }));
      toast.success(data.reminded
        ? t('pages.collaborators.thProfile.remindedToast')
        : t('pages.collaborators.thProfile.sentToast'), {
        description: t('pages.collaborators.thProfile.sentToastBody', { name: u.name }),
      });
    } catch (e) {
      const notPublished = e?.response?.status === 422
        && e?.response?.data?.detail === 'questionnaire_not_published';
      toast.error(notPublished
        ? t('pages.collaborators.thProfile.notPublishedError')
        : formatApiError(e));
    } finally { setThBusy(null); }
  };
  const requestThProfile = (u, state) => {
    // A completed profile requires explicit confirmation before an update request.
    if (state === 'completed') { setConfirmUpdateFor(u); return; }
    doRequestThProfile(u);
  };

  const [positions, setPositions] = useState([]);
  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: us }, { data: ds }, posRes] = await Promise.all([
        api.get('/client/users'),
        api.get('/client/departments'),
        api.get('/client/positions').catch(() => ({ data: [] })),
      ]);
      setPeople(us || []);
      setDepartments(ds || []);
      const pos = Array.isArray(posRes.data) ? posRes.data : (posRes.data?.items || []);
      const dmap = Object.fromEntries((ds || []).map((d) => [d.id, d.name_es || d.name]));
      setPositions(pos.filter((p) => !p.archived).map((p) => ({ ...p, department_name: dmap[p.department_id] || '' })));
    } catch (e) {
      toast.error(t('pages.collaborators.saveFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); loadThStates(); /* eslint-disable-next-line */ }, []);

  // CTH-005 — directory segmentation: active collaborators vs former vs deactivated.
  const segmentOf = (u) => {
    if (u.employment_status === 'former') return 'former';
    if (['inactive', 'disabled'].includes(u.status)) return 'inactive';
    return 'active';
  };
  const segCounts = people.reduce((acc, u) => { acc[segmentOf(u)] += 1; return acc; },
    { active: 0, former: 0, inactive: 0 });
  const filtered = people.filter((u) => {
    if (segmentOf(u) !== tab) return false;
    if (!q.trim()) return true;
    const term = q.toLowerCase();
    return (u.name || '').toLowerCase().includes(term)
      || (u.email || '').toLowerCase().includes(term)
      || (u.department_name || '').toLowerCase().includes(term);
  });
  const sorted = sort.apply(filtered, SORT_KEYS);

  const onAdd = async () => {
    // UAT 07/18 — B: explicit field-level validation, never a bare error.
    const errs = {};
    if (!draft.name.trim()) errs.name = t('pages.collaborators.validation.nameRequired', { defaultValue: 'Name is required.' });
    if (!draft.email.trim()) {
      errs.email = t('pages.collaborators.validation.emailRequired', { defaultValue: 'Email is required.' });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(draft.email.trim())) {
      errs.email = t('pages.collaborators.validation.emailInvalid', { defaultValue: 'The email address is not valid.' });
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(t('pages.collaborators.validation.title', { defaultValue: 'Please review the highlighted fields' }), {
        description: Object.values(errs).join(' '),
      });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/client/users', {
        name: draft.name.trim(),
        email: draft.email.trim(),
        position: draft.position.trim() || null,
        department_id: draft.department_id || null,
      });
      toast.success(t('pages.collaborators.createdToast'));
      setAddOpen(false);
      setDraft({ name: '', email: '', position: '', department_id: '' });
      setFieldErrors({});
      // UAT 07/18 — item 2: show the one-time temporary credentials.
      if (data?.temp_password) {
        setCreatedCreds({ email: data.user?.email, password: data.temp_password, name: data.user?.name });
      }
      reload();
    } catch (e) {
      // Iter 31 (R1) — surface user-limit cap with bilingual message
      const detail = e?.response?.data?.detail;
      const isES = (t('common.cancel') === 'Cancelar');
      if (detail && typeof detail === 'object' && detail.code === 'USER_LIMIT_REACHED') {
        toast.error(isES ? detail.message_es : detail.message_en, {
          description: `${isES ? 'Activos' : 'Active'}: ${detail.active} / ${detail.cap}`,
        });
      } else if (detail === 'A user with this email already exists') {
        setFieldErrors({ email: t('pages.collaborators.validation.emailExists', { defaultValue: 'A user with this email already exists.' }) });
        toast.error(t('pages.collaborators.validation.emailExists', { defaultValue: 'A user with this email already exists.' }));
      } else {
        toast.error(t('pages.collaborators.saveFailed'), { description: formatApiError(e) });
      }
    } finally { setSaving(false); }
  };

  return (
    <>
      <Topbar
        title={t('pages.collaborators.title')}
        subtitle={t('pages.collaborators.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            {user?.role === 'client_admin' && (
              <Button
                size="sm"
                variant="outline"
                data-testid="bulk-import-btn"
                onClick={() => setImportOpen(true)}
                className="gap-1.5"
              >
                <FileSpreadsheet className="h-4 w-4" /> {t('pages.collaborators.import.btn')}
              </Button>
            )}
            <Button
              size="sm"
              data-testid="add-person-btn"
              onClick={() => setAddOpen(true)}
              className="bg-gradient-primary hover:opacity-90 gap-1.5"
            >
              <Plus className="h-4 w-4" /> {t('pages.collaborators.addPerson')}
            </Button>
          </div>
        }
      />
      <OrgImportWizard open={importOpen} onClose={() => setImportOpen(false)} onImported={reload} />

      {/* Founder P1 2026-09 — Editar colaborador */}
      <Dialog open={!!editDraft} onOpenChange={(o) => { if (!o) setEditDraft(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="collab-edit-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.collaborators.edit.title', { defaultValue: 'Editar colaborador' })}</DialogTitle>
            <DialogDescription>
              {t('pages.collaborators.edit.desc', { defaultValue: 'Los cambios se aplican a la misma persona: su historial, evaluaciones y registros permanecen vinculados.' })}
            </DialogDescription>
          </DialogHeader>
          {editDraft && (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.collaborators.edit.secIdentity', { defaultValue: 'Identificación personal' })}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t('pages.collaborators.edit.name', { defaultValue: 'Nombre y apellido' })}</Label>
                    <Input value={editDraft.name} onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                           data-testid="collab-edit-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={editDraft.email} onChange={(e) => setEditDraft((d) => ({ ...d, email: e.target.value }))}
                           data-testid="collab-edit-email" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.collaborators.edit.secOrg', { defaultValue: 'Organización y puesto' })}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t('pages.collaborators.department', { defaultValue: 'Departamento' })}</Label>
                    <Select value={editDraft.department_id || '__none__'}
                            onValueChange={(v) => setEditDraft((d) => ({ ...d, department_id: v === '__none__' ? '' : v }))}>
                      <SelectTrigger data-testid="collab-edit-dept"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('pages.collaborators.edit.noDept', { defaultValue: '— Sin departamento' })}</SelectItem>
                        {departments.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{tData(d)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('pages.collaborators.edit.position', { defaultValue: 'Puesto (título)' })}</Label>
                    <Input value={editDraft.position} onChange={(e) => setEditDraft((d) => ({ ...d, position: e.target.value }))}
                           data-testid="collab-edit-position" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('pages.collaborators.edit.formalPosition', { defaultValue: 'Puesto formal (estructura organizacional)' })}</Label>
                  <FormalPositionPicker positions={positions} value={editDraft.position_id}
                                        onChange={(v) => setEditDraft((d) => ({ ...d, position_id: v }))}
                                        es={(i18n.language || '').startsWith('es')} testid="collab-edit-formal-position" />
                  <p className="text-[11px] text-muted-foreground">
                    {t('pages.collaborators.edit.formalPositionHint', { defaultValue: 'Vincular el puesto formal permite resolver automáticamente el Perfil de Puesto y sus competencias en TalentCompetency.' })}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={() => setEditDraft(null)} data-testid="collab-edit-cancel">
                  {t('common.cancel', { defaultValue: 'Cancelar' })}
                </Button>
                <Button onClick={saveEdit} disabled={editSaving} data-testid="collab-edit-save">
                  {editSaving ? '…' : t('common.save', { defaultValue: 'Guardar' })}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <div className="p-6 lg:p-8 space-y-5">
        <Card className="p-4 shadow-card flex flex-wrap items-center gap-3" data-testid="collaborators-toolbar">
          <div className="flex items-center gap-1.5" data-testid="collab-tabs">
            {[['active', t('pages.collaborators.tabActive', { defaultValue: 'Colaboradores activos' })],
              ['former', t('pages.collaborators.tabFormer', { defaultValue: 'Excolaboradores' })],
              ['inactive', t('pages.collaborators.tabInactive', { defaultValue: 'Desactivados' })]].map(([key, label]) => (
              <Button key={key} size="sm" variant={tab === key ? 'default' : 'outline'}
                      className="rounded-full h-8" onClick={() => setTab(key)}
                      data-testid={`collab-tab-${key}`}>
                {label} · {segCounts[key]}
              </Button>
            ))}
          </div>
          <div className="relative flex-1 min-w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('pages.collaborators.searchPh')}
              value={q}
              onChange={e=>setQ(e.target.value)}
              className="pl-9 h-10"
              data-testid="collab-search"
            />
          </div>
          <ListGridToggle value={view} onChange={setView} testIdPrefix="collab-view" />
        </Card>

        {loading ? (
          <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="collab-loading">
            {t('common.loading')}
          </Card>
        ) : people.length === 0 ? (
          <Card className="p-12 text-center shadow-card" data-testid="collab-empty-state">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="font-display text-xl font-bold mb-2">
              {t('pages.collaborators.emptyTitle', { defaultValue: 'No collaborators yet' })}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t('pages.collaborators.emptyBody', { defaultValue: 'Add your first collaborator to start running evaluations and building your talent map.' })}
            </p>
            <Button
              onClick={() => setAddOpen(true)}
              className="mt-6 bg-gradient-primary hover:opacity-90 gap-1.5"
              data-testid="collab-empty-add-btn"
            >
              <Plus className="h-4 w-4" /> {t('pages.collaborators.addPerson')}
            </Button>
          </Card>
        ) : sorted.length === 0 ? (
          <Card className="p-10 text-center text-sm text-muted-foreground shadow-card" data-testid="collab-tab-empty">
            {tab === 'former'
              ? t('pages.collaborators.emptyFormer', { defaultValue: 'No hay excolaboradores registrados.' })
              : tab === 'inactive'
                ? t('pages.collaborators.emptyInactive', { defaultValue: 'No hay usuarios desactivados.' })
                : t('pages.collaborators.emptyFiltered', { defaultValue: 'Sin resultados para la búsqueda actual.' })}
          </Card>
        ) : view === 'grid' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="collab-grid">
            {sorted.map((u) => (
              <Card key={u.id} className="p-5 shadow-card hover:shadow-medium transition-smooth flex flex-col" data-testid={`collab-card-${u.id}`}>
                <div className="flex items-start gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-gradient-primary text-white text-sm font-bold">{initials(u.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{u.name}</p>
                    {u.employment_status === 'former' && (
                      <Badge variant="outline" className="mt-1 text-xs bg-zinc-100 text-zinc-600 border-zinc-300" data-testid={`collab-former-badge-${u.id}`}>
                        {t('pages.collaborators.formerBadge', { defaultValue: 'Excolaborador' })}
                      </Badge>
                    )}
                    <p className="text-xs text-muted-foreground truncate">{tData(u.position)}</p>
                    {u.department_name && <Badge variant="outline" className="mt-2 font-medium text-xs">{tData({ name: u.department_name, name_es: u.department_name_es })}</Badge>}
                  </div>
                </div>
                <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{u.email}</div>
                </div>
                <div className="mt-auto pt-4 flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    data-testid={`collab-view-profile-${u.id}`}
                    onClick={() => setViewing(u)}
                  >
                    {t('pages.collaborators.viewProfile')}
                  </Button>
                  {(!u.role || u.role === 'evaluated') && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={issuingCreds === u.id}
                    title={t('pages.collaborators.portalAccess', { defaultValue: 'Portal access — issue temporary credentials' })}
                    data-testid={`collab-issue-creds-${u.id}`}
                    onClick={() => doIssueCreds(u)}
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                  </Button>
                  )}
                  <ThProfileAction
                    u={u}
                    state={thStates[u.id]?.state}
                    busy={thBusy === u.id}
                    canRequest={canRequestTh}
                    onRequest={requestThProfile}
                    t={t}
                  />
                </div>
              </Card>
            ))}
            {!sorted.length && (
              <Card className="p-8 text-center text-sm text-muted-foreground italic col-span-full">
                {t('pages.collaborators.noResults')}
              </Card>
            )}
          </div>
        ) : (
          <Card className="shadow-card overflow-hidden" data-testid="collab-list">
            {/* Founder UAT (M) — accessible horizontal + vertical scrolling at 100% zoom:
                min table width, sticky header, scrollbar reachable while working. */}
            <div className="overflow-auto max-h-[72vh]" data-testid="collab-list-scroll">
            <table className="w-full min-w-[880px] text-sm">
              <thead className="sticky top-0 z-10 bg-card shadow-sm text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-5 py-3"><SortableHeader sort={sort} k="collaborator" testIdPrefix="collab">{t('pages.collaborators.th.collaborator')}</SortableHeader></th>
                  <th className="px-5 py-3 hidden md:table-cell"><SortableHeader sort={sort} k="position" testIdPrefix="collab">{t('pages.collaborators.th.position')}</SortableHeader></th>
                  <th className="px-5 py-3 hidden md:table-cell"><SortableHeader sort={sort} k="department" testIdPrefix="collab">{t('pages.collaborators.th.department')}</SortableHeader></th>
                  <th className="px-5 py-3 hidden lg:table-cell"><SortableHeader sort={sort} k="contact" testIdPrefix="collab">{t('pages.collaborators.th.contact')}</SortableHeader></th>
                  <th className="px-5 py-3 w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sorted.map((u) => (
                  <tr key={u.id} className="hover:bg-muted/30" data-testid={`collab-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9"><AvatarFallback className="bg-gradient-primary text-white text-xs font-bold">{initials(u.name)}</AvatarFallback></Avatar>
                        <div>
                          <p className="font-semibold">
                            {u.name}
                            {u.employment_status === 'former' && (
                              <Badge variant="outline" className="ml-2 text-xs bg-zinc-100 text-zinc-600 border-zinc-300" data-testid={`collab-row-former-badge-${u.id}`}>
                                {t('pages.collaborators.formerBadge', { defaultValue: 'Excolaborador' })}
                              </Badge>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-muted-foreground">{tData(u.position)}</td>
                    <td className="px-5 py-3 hidden md:table-cell">{u.department_name && <Badge variant="outline" className="text-xs whitespace-nowrap">{tData({ name: u.department_name, name_es: u.department_name_es })}</Badge>}</td>
                    <td className="px-5 py-3 hidden lg:table-cell text-xs text-muted-foreground">{u.email}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`collab-view-profile-row-${u.id}`}
                          onClick={() => setViewing(u)}
                        >
                          {t('pages.collaborators.view')}
                        </Button>
                        {(!u.role || u.role === 'evaluated') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={issuingCreds === u.id}
                          title={t('pages.collaborators.portalAccess', { defaultValue: 'Portal access — issue temporary credentials' })}
                          data-testid={`collab-issue-creds-row-${u.id}`}
                          onClick={() => doIssueCreds(u)}
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                        </Button>
                        )}
                        <ThProfileAction
                          u={u}
                          state={thStates[u.id]?.state}
                          busy={thBusy === u.id}
                          canRequest={canRequestTh}
                          onRequest={requestThProfile}
                          t={t}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {!sorted.length && (
                  <tr><td colSpan={5} className="px-5 py-6 text-center text-muted-foreground italic">{t('pages.collaborators.noResults')}</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </Card>
        )}
      </div>

      {/* BUG 13b — Add person dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-person-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.collaborators.addTitle')}</DialogTitle>
            <DialogDescription>{t('pages.collaborators.addDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t('pages.profile.fullName')} <span className="text-destructive">*</span></Label>
              <Input value={draft.name} onChange={(e)=>setDraft({...draft, name:e.target.value})} data-testid="add-person-name" className={fieldErrors.name ? 'border-destructive' : ''} />
              {fieldErrors.name && <p className="text-xs text-destructive mt-1" data-testid="add-person-name-error">{fieldErrors.name}</p>}
            </div>
            <div>
              <Label className="text-xs">{t('pages.profile.email')} <span className="text-destructive">*</span></Label>
              <Input type="email" value={draft.email} onChange={(e)=>setDraft({...draft, email:e.target.value})} data-testid="add-person-email" className={fieldErrors.email ? 'border-destructive' : ''} />
              {fieldErrors.email && <p className="text-xs text-destructive mt-1" data-testid="add-person-email-error">{fieldErrors.email}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('pages.collaborators.th.position')} <span className="text-muted-foreground font-normal">({t('pages.collaborators.optional', { defaultValue: 'optional' })})</span></Label>
                <Input value={draft.position} onChange={(e)=>setDraft({...draft, position:e.target.value})} data-testid="add-person-position" />
              </div>
              <div>
                <Label className="text-xs">{t('pages.collaborators.th.department')} <span className="text-muted-foreground font-normal">({t('pages.collaborators.optional', { defaultValue: 'optional' })})</span></Label>
                <Select value={draft.department_id} onValueChange={(v)=>setDraft({...draft, department_id: v === '__none__' ? '' : v})}>
                  <SelectTrigger data-testid="add-person-department"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAddOpen(false)}>{t('pages.collaborators.close')}</Button>
            <Button onClick={onAdd} disabled={saving} data-testid="add-person-save" className="bg-gradient-primary">{saving ? t('common.saving') : t('pages.collaborators.addPerson')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BUG 13c — View profile dialog */}
      <Dialog open={!!viewing} onOpenChange={(o)=>!o && setViewing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="view-profile-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{t('pages.collaborators.profileTitle')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16"><AvatarFallback className="bg-gradient-primary text-white text-lg font-bold">{initials(viewing.name)}</AvatarFallback></Avatar>
                  <div className="flex-1">
                    <p className="font-display text-lg font-bold tracking-tight">{viewing.name}</p>
                    <p className="text-sm text-muted-foreground">{tData(viewing.position)}</p>
                    {viewing.department_name && <Badge variant="outline" className="mt-1 text-xs">{tData({ name: viewing.department_name, name_es: viewing.department_name_es })}</Badge>}
                  </div>
                  {user?.role === 'client_admin' && !viewing.external_candidate && (
                    <Button variant="outline" size="sm" className="gap-1.5"
                            onClick={() => { setEditDraft({
                              id: viewing.id,
                              name: viewing.name || '',
                              email: viewing.email || '',
                              department_id: viewing.department_id || '',
                              position: typeof viewing.position === 'string' ? viewing.position : (viewing.position?.name || ''),
                              position_id: viewing.position_id || '',
                            }); }}
                            data-testid={`collab-edit-${viewing.id}`}>
                      {t('pages.collaborators.edit.btn', { defaultValue: 'Editar colaborador' })}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.profile.email')}</p>
                    <p className="text-foreground break-all">{viewing.email}</p>
                  </div>
                  {viewing.role && (
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.collaborators.roleLabelTitle', { defaultValue: 'Role' })}</p>
                      <p className="text-foreground">
                        {viewing.external_candidate
                          ? t('pages.collaborators.roleLabel.external_candidate', { defaultValue: 'Candidato externo' })
                          : viewing.role === 'evaluated'
                            ? t('pages.collaborators.roleLabel.collaborator', { defaultValue: 'Colaborador' })
                            : t(`pages.collaborators.roleLabel.${viewing.role}`, { defaultValue: viewing.role })}
                      </p>
                    </div>
                  )}
                </div>

                {/* CTH-HEART-061 — Conoce a tu colaborador: actual consented values */}
                {(!viewing.role || viewing.role === 'evaluated') && !viewing.external_candidate && knowPrefs && (
                  <div className="rounded-lg border border-teal-200/70 bg-teal-50/40 dark:bg-teal-500/5 p-3 space-y-2"
                       data-testid="collab-know-section">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-700 flex items-center gap-1">
                      <Heart className="h-3 w-3" /> {t('pages.collaborators.know.title', { defaultValue: 'Conoce a tu colaborador' })}
                    </p>
                    {knowPrefs.shared ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid="collab-know-values">
                        {[['favorite_color', t('pages.collaborators.know.color', { defaultValue: 'Color favorito' })],
                          ['favorite_dessert', t('pages.collaborators.know.dessert', { defaultValue: 'Postre favorito' })],
                          ['favorite_food', t('pages.collaborators.know.food', { defaultValue: 'Comida favorita' })],
                          ['favorite_sport', t('pages.collaborators.know.sport', { defaultValue: 'Deporte favorito' })],
                          ['favorite_team', t('pages.collaborators.know.team', { defaultValue: 'Equipo favorito' })],
                          ['celebration_preferences', t('pages.collaborators.know.celebration', { defaultValue: 'Cómo le gusta celebrar' })]]
                          .filter(([k]) => knowPrefs.preferences[k])
                          .map(([k, label]) => (
                            <div key={k}>
                              <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
                              <p data-testid={`collab-know-${k}`}>{knowPrefs.preferences[k]}</p>
                            </div>
                          ))}
                        {Array.isArray(knowPrefs.preferences.hobbies) && knowPrefs.preferences.hobbies.length > 0 && (
                          <div className="sm:col-span-2">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.know.hobbies', { defaultValue: 'Pasatiempos' })}</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {knowPrefs.preferences.hobbies.map((h) => (
                                <Badge key={h} variant="outline" className="text-xs">{h}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {Object.keys(knowPrefs.preferences).length === 0 && (
                          <p className="text-xs text-muted-foreground sm:col-span-2">
                            {t('pages.collaborators.know.emptyShared', { defaultValue: 'El colaborador autorizó compartir, pero aún no registra preferencias.' })}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="collab-know-noconsent">
                        {t('pages.collaborators.know.noConsent', { defaultValue: 'El colaborador no ha autorizado compartir sus gustos y preferencias. Los valores solo se muestran con su consentimiento expreso (revocable desde Mi Perfil).' })}
                      </p>
                    )}
                  </div>
                )}

                {/* TalentHeart operational status — privacy-safe, no wellbeing answers */}
                {(!viewing.role || viewing.role === 'evaluated') && !viewing.external_candidate && (() => {
                  const st = thStates[viewing.id] || {};
                  const state = st.state || 'not_requested';
                  const bday = st.birthday || 'not_collected';
                  return (
                    <div className="rounded-lg border border-pink-200/60 bg-pink-50/40 dark:bg-pink-500/5 p-3 space-y-2"
                         data-testid="collab-th-profile-section">
                      <p className="text-xs uppercase tracking-wider font-semibold text-pink-600 flex items-center gap-1">
                        <Heart className="h-3 w-3" /> {t('pages.collaborators.thProfile.sectionTitle')}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.thProfile.statusLabel')}</p>
                          <p data-testid="collab-th-detail-state">{t(`pages.collaborators.thProfile.stateLong.${state}`)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.thProfile.lastInvite')}</p>
                          <p data-testid="collab-th-detail-invite">{fmtDateTimePA(st.requested_at, i18n.language)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.thProfile.lastReminder')}</p>
                          <p data-testid="collab-th-detail-reminder">{fmtDateTimePA(st.reminded_at, i18n.language)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.thProfile.completedAt')}</p>
                          <p data-testid="collab-th-detail-completed">{fmtDateTimePA(st.completed_at, i18n.language)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.thProfile.birthdayLabel')}</p>
                          <p data-testid="collab-th-detail-birthday">{t(`pages.collaborators.thProfile.birthday.${bday}`)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.thProfile.questionnaireLabel')}</p>
                          <p data-testid="collab-th-detail-questionnaire">
                            {thQuestionnaire?.published
                              ? t('pages.collaborators.thProfile.questionnairePublished')
                              : t('pages.collaborators.thProfile.questionnaireDraft')}
                          </p>
                        </div>
                        {(st.categories || []).length > 0 && (
                          <div className="sm:col-span-2">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.thProfile.categoriesLabel')}</p>
                            <p data-testid="collab-th-detail-categories">
                              {(st.categories || []).map((c) => t(`clientTalentHeart.interests.${c}`, { defaultValue: c })).join(' · ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setViewing(null)}>{t('pages.collaborators.close')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Explicit confirmation before requesting an update of a COMPLETED profile */}
      <Dialog open={!!confirmUpdateFor} onOpenChange={(v) => !v && setConfirmUpdateFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="collab-th-confirm-update">
          <DialogHeader>
            <DialogTitle>{t('pages.collaborators.thProfile.confirmUpdateTitle')}</DialogTitle>
            <DialogDescription>
              {t('pages.collaborators.thProfile.confirmUpdateBody', { name: confirmUpdateFor?.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmUpdateFor(null)} data-testid="collab-th-confirm-cancel">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => { const u = confirmUpdateFor; setConfirmUpdateFor(null); doRequestThProfile(u); }}
              data-testid="collab-th-confirm-ok"
            >
              {t('pages.collaborators.thProfile.confirmUpdateCta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UAT 07/18 — item 2: one-time temporary credentials dialog. */}
      <Dialog open={!!createdCreds} onOpenChange={(v) => !v && setCreatedCreds(null)}>
        <DialogContent className="sm:max-w-md" data-testid="collab-credentials-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.collaborators.creds.title', { defaultValue: 'Temporary credentials' })}</DialogTitle>
            <DialogDescription>
              {t('pages.collaborators.creds.desc', { defaultValue: 'Share these credentials with the collaborator. They will be required to change the password at first login.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.profile.email')}</p>
                <p className="text-sm font-mono" data-testid="collab-creds-email">{createdCreds?.email}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.collaborators.creds.tempPassword', { defaultValue: 'Temporary password' })}</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono" data-testid="collab-creds-password">{createdCreds?.password}</p>
                  <Button
                    variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => { navigator.clipboard.writeText(createdCreds?.password || ''); toast.success(t('pages.collaborators.creds.copied', { defaultValue: 'Password copied' })); }}
                    data-testid="collab-creds-copy"
                  >
                    {t('pages.collaborators.creds.copy', { defaultValue: 'Copy' })}
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-500" data-testid="collab-creds-warning">
              {t('pages.collaborators.creds.warning', { defaultValue: 'This password is shown only once for onboarding purposes and cannot be retrieved later.' })}{' '}
              {t('pages.collaborators.creds.rotates', { defaultValue: 'Generating new credentials replaces any previously issued temporary password.' })}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedCreds(null)} data-testid="collab-creds-close">
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
